import { useTranslation } from "react-i18next";
import { FileText } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";

import { formatNumber } from "../../../../utils";
import {
  deliveredQuantity,
  formatOfferDateTime,
  offerDeliveryNoteUrl,
  offerDisplayStatus,
} from "../../utils";
import { offerCurrencySymbol } from "../OfferCard";
import { DeliveryConfirmation, DeliverySide, Offer, OfferStatus } from "../OfferCard/interfaces";

interface DeliveryDetailsPanelProps {
  offer: Offer;
  readOnly?: boolean;
}

const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <div className="flex justify-between gap-4 px-4 py-2.5">
    <dt className="text-muted-foreground text-sm">{label}</dt>
    <dd className="text-right font-medium text-foreground text-sm">{value}</dd>
  </div>
);

export const hasDeliveryDetails = (offer: Offer): boolean =>
  !!offer.delivery_recorded_by || !!offer.delivery_rejection;

export const DeliveryDetailsPanel = ({ offer, readOnly = false }: DeliveryDetailsPanelProps) => {
  const { t, i18n } = useTranslation();

  if (!hasDeliveryDetails(offer)) {
    return null;
  }

  const currency = offerCurrencySymbol(t);
  const unit = offer.unit_of_measure?.code ?? "";
  const sideName = (side: DeliverySide | null | undefined) =>
    side === "BUYER" ? offer.trader?.name : offer.tagged_aggregator?.name;
  const status = offerDisplayStatus(offer, readOnly);
  const rejection = offer.delivery_rejection;
  const disputed =
    offer.delivery_confirmation === DeliveryConfirmation.DisputedByAggregator ||
    offer.delivery_confirmation === DeliveryConfirmation.DisputedByBuyer;
  const disputedBy: DeliverySide =
    offer.delivery_confirmation === DeliveryConfirmation.DisputedByBuyer ? "BUYER" : "AGGREGATOR";
  const paid = !!offer.final_payment_amount && offer.status !== OfferStatus.ExchangePaymentPending;
  const method =
    offer.delivery_payment_method_details?.name ?? offer.exchange?.payment_method_details?.name;
  const noteUrl = offerDeliveryNoteUrl(offer);

  return (
    <section
      aria-label={t("deliveryDetails.title")}
      className="mx-6 my-4 overflow-hidden rounded-lg border border-border"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-border border-b bg-muted/40 px-4 py-2.5">
        <span className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">
          {t("deliveryDetails.title")}
        </span>
        <Badge variant={status.variant}>{t(status.labelKey, status.params)}</Badge>
      </div>

      {offer.delivery_recorded_by && (
        <dl className="divide-y divide-border">
          <DetailRow
            label={t("deliveryDetails.recordedBy")}
            value={[
              sideName(offer.delivery_recorded_by),
              formatOfferDateTime(offer.delivery_recorded_at),
            ]
              .filter(Boolean)
              .join(" · ")}
          />
          <DetailRow
            label={t("deliveryDetails.quantity")}
            value={`${formatNumber(deliveredQuantity(offer))} ${unit}`}
          />
          <DetailRow
            label={t("deliveryDetails.payment")}
            value={
              paid
                ? `${currency}${formatNumber(Number(offer.final_payment_amount))}`
                : t("deliveryDetails.notPaidYet")
            }
          />
          {paid && method && <DetailRow label={t("deliveryDetails.method")} value={method} />}
          {paid && offer.includes_delivery_cost && (
            <DetailRow
              label={t("deliveryDetails.deliveryCost")}
              value={t("deliveryDetails.deliveryCostIncluded")}
            />
          )}
          {offer.delivery_confirmation === DeliveryConfirmation.Confirmed && (
            <DetailRow
              label={t("deliveryDetails.confirmedAt")}
              value={formatOfferDateTime(offer.delivery_confirmed_at)}
            />
          )}
          {noteUrl && (
            <div className="flex justify-between gap-4 px-4 py-2.5">
              <dt className="text-muted-foreground text-sm">{t("deliveryDetails.note")}</dt>
              <dd>
                <a
                  href={noteUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 font-medium text-primary text-sm underline"
                >
                  <FileText className="size-4" />
                  {t("deliveryDetails.viewNote")}
                </a>
              </dd>
            </div>
          )}
        </dl>
      )}

      {disputed && (
        <div className="p-4">
          <Alert variant="destructive">
            <AlertTitle>
              {t("deliveryDetails.disputedBy", {
                name: sideName(disputedBy),
                date: formatOfferDateTime(offer.delivery_disputed_at),
              })}
            </AlertTitle>
            <AlertDescription className="flex flex-col gap-1">
              <span>
                {(offer.delivery_dispute_topics ?? [])
                  .map(topic => t(`deliveryTopics.${topic}`))
                  .join(", ")}
              </span>
              {offer.delivery_dispute_comment && (
                <span className="whitespace-pre-wrap">{offer.delivery_dispute_comment}</span>
              )}
            </AlertDescription>
          </Alert>
        </div>
      )}

      {rejection && (
        <div className="p-4">
          <Alert variant="destructive">
            <AlertTitle>
              {t("deliveryDetails.rejectedBy", {
                name: sideName(rejection.side),
                date: formatOfferDateTime(rejection.rejected_at),
              })}
            </AlertTitle>
            <AlertDescription className="flex flex-col gap-1">
              <span>{rejection.reason_translations?.[i18n.language] ?? rejection.reason_text}</span>
              {rejection.comment && (
                <span className="whitespace-pre-wrap">{rejection.comment}</span>
              )}
              {rejection.rejected_by_name && (
                <span className="text-xs">{rejection.rejected_by_name}</span>
              )}
            </AlertDescription>
          </Alert>
        </div>
      )}
    </section>
  );
};
